import React, { useEffect } from 'react';
import Loader from 'common/elements/Loader/Loader';
import { debounce } from 'lodash/function';
import { isEmpty } from 'lodash/lang';

import ApplyAndResetButtons from 'components/ApplyAndResetButtons/ApplyAndResetButtons';
import BasicMultiselect from 'components/eui/EuiFiltersContext/controls/BasicMultiselect/BasicMultiselect';
import StudySiteMultiselect from 'components/eui/EuiFiltersContext/controls/StudySiteMultiselect/StudySiteMultiselect';
import { useEuiFiltersDispatch, useEuiFiltersState } from 'components/eui/EuiFiltersContext/EuiFiltersContext';
import { setCacheProcessor, setInitialStateProcessor } from 'components/eui/EuiFiltersContext/processors';
import { FRS_READY } from 'components/eui/EuiFiltersContext/reducer';
import { PageInfoHeader } from 'components/PageInfoHeader/PageInfoHeader';
import { INITIAL_FILTERS_STATE } from 'components/pages/patient-source/TaskWorklist/constants';
import Assignee from 'components/pages/patient-source/TaskWorklist/FiltersSection/Assignee/Assignee';

import { TaskApi } from '../../../../../api';
import Button from '../../../../../common/general/Button';
import { EXPORT_TASKS } from '../../../../../constants/userOperations';
import { userHasAccessTo } from '../../../../../services/auth';

const debounceCache = debounce(cb => cb(), 500);

export default function FiltersSection({ gridData, onApply, applyDisabled, onReset, exportTasks }) {
  const state = useEuiFiltersState();
  const dispatch = useEuiFiltersDispatch();

  const { schema } = state;
  const status = state.schema?.status;

  useEffect(
    function() {
      if (status !== FRS_READY) {
        return;
      }
      debounceCache(function() {
        dispatch({
          type: 'PERFORM',
          processor: setCacheProcessor
        });
      });
    },
    [dispatch, gridData, status]
  );

  useEffect(
    function() {
      if (status) return;
      dispatch({
        type: 'PERFORM_INITIALIZATION',
        processor: setInitialStateProcessor,
        payload: {
          ...INITIAL_FILTERS_STATE,
          cacheCollector
        }
      });
    },
    [dispatch, status]
  );

  if (isEmpty(schema)) return null;

  return (
    <PageInfoHeader
      right={
        userHasAccessTo(EXPORT_TASKS) && (
          <Button size="h28" onClick={exportTasks} priority="medium">
            Export
          </Button>
        )
      }
    >
      <div className="general-header-wrapper" style={{ minHeight: 71 }}>
        <BasicMultiselect uniqKey="statuses" state={state} dispatch={dispatch} label="Status" />
        <BasicMultiselect
          uniqKey="topics"
          state={state}
          dispatch={dispatch}
          label="Topics"
          getOptions={getTaskTopics}
        />
        <StudySiteMultiselect uniqKey="ssu" state={state} dispatch={dispatch} getStudySites={getStudySites} />
        <Assignee uniqKey="assignee" state={state} dispatch={dispatch} />
        {status === FRS_READY ? (
          <ApplyAndResetButtons
            applyButtonId="task-worklist-filter-apply-btn"
            resetButtonId="task-worklist-filter-reset-btn"
            applyClassName="apply-btn"
            resetClassName="pi-worklist-refresh-btn"
            onApply={onApply}
            applyDisabled={applyDisabled}
            onReset={onReset}
          />
        ) : (
          <Loader dark={true} />
        )}
      </div>
    </PageInfoHeader>
  );
}

async function getStudySites() {
  const { data: studySites } = await TaskApi.getAllStudySites();
  return { studySites };
}

async function getTaskTopics() {
  return TaskApi.getTaskTopics().then(({ data }) => ({ source: data, value: data }));
}

function cacheCollector(state) {
  return {
    statuses: {
      value: state?.statuses?.value
    },
    topics: {
      value: state?.topics?.value
    },
    ssu: {
      selectedStudyMap: state?.ssu?.selectedStudyMap,
      selectedSiteMap: state?.ssu?.selectedSiteMap
    },
    assignee: {
      value: state?.assignee?.value
    },
    table: state?.table
  };
}
